// Swedish bankgiro numbers: 7 or 8 digits, the last one a mod-10 (Luhn) check digit.

const GROUPING_CHARS = /[\s-]/g;
/** A label pasted along with the number, e.g. "BG 5050-1055" or "Bankgiro: 5050-1055". */
const PREFIX = /^\s*(?:bankgiro|bg)[\s:.]*/i;
const MAX_DIGITS = 8;

/** Digits only, or null when the input is not a 7–8 digit bankgiro in any accepted notation. */
export function normalizeBankgiro(input: string): string | null {
  const digits = input.replace(PREFIX, "").replace(GROUPING_CHARS, "");
  return /^\d{7,8}$/.test(digits) ? digits : null;
}

/** Expects normalized digits; a hyphenated or spaced value is not valid here. */
export function isValidBankgiro(digits: string): boolean {
  if (!/^\d{7,8}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let d = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

/** NNN-NNNN for 7 digits, NNNN-NNNN for 8. */
export function formatBankgiro(digits: string): string {
  return `${digits.slice(0, -4)}-${digits.slice(-4)}`;
}

export const BANKGIRO_INVALID_MESSAGE = "This isn't a valid bankgiro number. Check the last digit.";

const digitsOf = (value: string) => value.replace(/\D/g, "");

/**
 * What a bankgiro field shows after an edit: at most 8 digits, hyphenated once 7 or 8 are typed.
 * An edit that would add a 9th digit, or a character that can't be part of a bankgiro, is
 * refused and the field keeps `previous`.
 */
export function maskBankgiroInput(next: string, previous: string): string {
  const rest = next.replace(PREFIX, "");
  if (/[^\d\s-]/.test(rest)) return previous;
  let digits = digitsOf(rest);
  if (digits.length > MAX_DIGITS) return previous;
  // Backspace over the inserted hyphen removes the digit before it, rather than nothing.
  if (digits === digitsOf(previous) && next.length < previous.length) {
    const changedAt = [...next].findIndex((ch, i) => ch !== previous[i]);
    const digitsBefore = digitsOf(previous.slice(0, changedAt === -1 ? next.length : changedAt)).length;
    digits = digits.slice(0, Math.max(digitsBefore - 1, 0)) + digits.slice(digitsBefore);
  }
  return digits.length >= 7 ? formatBankgiro(digits) : digits;
}

/**
 * Live feedback for a bankgiro field: the printable form once valid, and a reason only once the
 * number is complete (8 digits, or 7 after the user leaves the field), never while typing.
 */
export function bankgiroInputFeedback(
  typed: string,
  { blurred = false }: { blurred?: boolean } = {},
): { error: string | null; formatted: string | null } {
  const digits = normalizeBankgiro(typed);
  if (digits && isValidBankgiro(digits)) return { error: null, formatted: formatBankgiro(digits) };
  const typedDigits = digitsOf(typed.replace(PREFIX, "")).length;
  const complete = typedDigits >= MAX_DIGITS || (blurred && typedDigits === 7);
  return { error: complete ? BANKGIRO_INVALID_MESSAGE : null, formatted: null };
}
